import { AuthResponse, MccsRoles } from '@/@types/generated'
import { Feature } from './feature'
import { hasRole } from '@/components/Atoms/Permissions'
import { LicensingAgentsFeature } from './licensing-agents'
import { InternalRoles } from '../utils'

export const CertificatesFeature = {
  // Roles that are eligible for the Certificates feature
  Roles: [MccsRoles.MOTOR_CARRIER_OWNER, MccsRoles.MOTOR_CARRIER_USER],

  // Determine if the feature is enabled for the given user
  isEnabled: (user: AuthResponse | null): boolean =>
    hasRole(CertificatesFeature.Roles, user),

  // Sub-features

  Application: {
    // Anyone can apply for a certificate, UNLESS:
    // - They are a licensing agency (certificates must be applied by the actual carrier)
    // - They are an internal user (certificates must be applied by the actual carrier)
    // TODO: If they are already an insurance provider?
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole([...LicensingAgentsFeature.Roles, ...InternalRoles], user, true),
  } satisfies Feature,

  View: {
    // Internal users and MC owner/user can view the certificate
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole(
        [
          ...InternalRoles,
          MccsRoles.MOTOR_CARRIER_OWNER,
          MccsRoles.MOTOR_CARRIER_USER,
        ],
        user
      ),
  } satisfies Feature,

  Manage: {
    // Only allow updating certificate details for:
    // - They are a credentialing user (certificates must be applied by the actual carrier)
    // - They are the owner/user
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole(
        [
          MccsRoles.CREDENTIALING_ADMIN,
          MccsRoles.CREDENTIALING,
          MccsRoles.MOTOR_CARRIER_OWNER,
          MccsRoles.MOTOR_CARRIER_USER,
        ],
        user
      ),
  } satisfies Feature,

  // Authorize a licensing agency on the carrier's business entity. Matches
  // BusinessEntityController.AssignLicensingAgency's authorization on the backend —
  // MotorCarrierOwner + internal roles, NOT MotorCarrierUser.
  AuthorizeLicensingAgency: {
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole([...InternalRoles, MccsRoles.MOTOR_CARRIER_OWNER], user),
  } satisfies Feature,

  Revoke: {
    // Only credentialing users/admins can revoke
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole([MccsRoles.CREDENTIALING_ADMIN, MccsRoles.CREDENTIALING], user),
  } satisfies Feature,

  ReActivate: {
    // Only credentialing users/admins can re-activate a Revoked-Unsatisfactory certificate
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole([MccsRoles.CREDENTIALING_ADMIN, MccsRoles.CREDENTIALING], user),
  } satisfies Feature,

  // Which roles can manage certificate addresses?
  Addresses: {
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole(
        [
          MccsRoles.CREDENTIALING_ADMIN,
          MccsRoles.CREDENTIALING,
          MccsRoles.MOTOR_CARRIER_OWNER,
        ],
        user
      ),
  } satisfies Feature,

  ViewMcs150Email: {
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole(
        [
          MccsRoles.MOTOR_CARRIER_OWNER,
          MccsRoles.CREDENTIALING_ADMIN,
          MccsRoles.CREDENTIALING,
        ],
        user
      ),
  },

  // HHG Tariff tab (household goods insurance coverage tier) — TxDMV
  // credentialing staff and licensing agencies only
  HhgTariff: {
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole(
        [
          ...InternalRoles,
          MccsRoles.MOTOR_CARRIER_OWNER,
          MccsRoles.MOTOR_CARRIER_USER,
          MccsRoles.LICENSING_AGENCY_OWNER,
          MccsRoles.LICENSING_AGENCY_USER,
        ],
        user
      ),
  } satisfies Feature,
} satisfies Feature
